import { useRef, useState } from 'react';
import Keycloak from 'keycloak-js';
import { getRealmFromUrl } from '../utils';

export interface Tokens {
  token?: string;
  refreshToken?: string;
  idToken?: string;
}

export interface Returns {
  keycloak?: Keycloak;
  init: () => Promise<void>;
  getTokens: () => Tokens;
  isInitialized: boolean;
}

export default function useKeycloak(): Returns {
  let keycloak = useRef<Keycloak | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);

  const saveTokens = () => {
    localStorage.setItem('token', keycloak.current?.token ?? '');
    localStorage.setItem(
      'refreshToken',
      keycloak.current?.refreshToken ?? '',
    );
    localStorage.setItem('idToken', keycloak.current?.idToken ?? '');
  };
  const getTokens = () => {
    return {
      token: localStorage.getItem('token') ?? undefined,
      refreshToken: localStorage.getItem('refreshToken') ?? undefined,
      idToken: localStorage.getItem('idToken') ?? undefined,
    };
  };
  const deleteTokens = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('login');
  };

  const init = async () => {
    const customUrls = JSON.parse(process.env.REACT_APP_CUSTOM_KEYCLOAK_URLS ?? '{}');
    const realm = getRealmFromUrl();
    keycloak.current = new Keycloak({
      url: customUrls[realm] ?? process.env.REACT_APP_KEYCLOAK_URL ?? '',
      realm: realm ?? process.env.REACT_APP_KEYCLOAK_REALM_NAME ?? '',
      clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID ?? '',
    });

    try {
      keycloak.current.onTokenExpired = () => {
        keycloak.current
          ?.updateToken(30)
          .then((refreshed: boolean) => {
            if (!refreshed) {
              console.log('Failed to refresh token');
              deleteTokens();
              keycloak.current?.logout();
              keycloak.current?.login();
            } else saveTokens();
          })
          .catch((e: any) => {
            console.log('Failed to refresh token', e);
            deleteTokens();
            keycloak.current?.logout();
            keycloak.current?.login();
          });
      };

      const isAuthenticated = await keycloak.current.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html',
        silentCheckSsoFallback: false,
        responseMode: 'query',
        redirectUri:
          window.location.origin +
          window.location.pathname +
          window.location.search +
          window.location.hash,
        checkLoginIframe: false,
        ...getTokens(),
      });

      // Pulisci i params Keycloak dall'URL dopo il code exchange
      const cleanSearch = new URLSearchParams(window.location.search);
      cleanSearch.delete('code');
      cleanSearch.delete('session_state');
      cleanSearch.delete('iss');
      const cleanSearchStr = cleanSearch.toString() ? `?${cleanSearch.toString()}` : '';
      const cleanUrl =
        window.location.origin +
        window.location.pathname +
        cleanSearchStr +
        window.location.hash;
      window.history.replaceState({}, '', cleanUrl);

      setIsInitialized(true);
      if (!isAuthenticated) {
        console.log('Not authenticated');
        deleteTokens();
        keycloak.current?.logout();
        keycloak.current?.login();
      }

      saveTokens();
    } catch (e) {
      console.log('Keycloak init', e);
    }
  };

  return { keycloak: keycloak.current, init, getTokens, isInitialized };
}
